import React from 'react';
import GenBanner, {
  BannerBorderType,
  BannerColorScheme,
  BannerParagraphAlignment,
  ICON_DESKTOP_SIZE_MAPPING,
  ICON_MOBILE_SIZE_MAPPING,
  ICON_STYLES,
} from './GenBanner';
import { TypeBannerFields } from '@/core/generated-types';
import { HeaderSize } from '@/components/ui/HeaderText';
import { LuvImage } from '@/components/ui';
import {
  CreditCardIcon,
  GiftIcon,
  InfoIcon,
  PackageIcon,
  WarningIcon,
} from '@/components/ui/Icons';
import { RecurringPackageIcon } from '@/components/ui/Icons';
import { CTA_COLORS } from '@/components/common/types';

const CTA_COLOR_MAPPING = {
  success: CTA_COLORS.DEFAULT,
  notice: CTA_COLORS.DEFAULT,
  error: CTA_COLORS.DEFAULT,
  holiday: CTA_COLORS.RED,
  upsell: CTA_COLORS.DEFAULT,
};

const iconComponents = {
  notice: InfoIcon,
  card: CreditCardIcon,
  gift: GiftIcon,
  package: PackageIcon,
  warning: WarningIcon,
  recurringPackage: RecurringPackageIcon,
};

export interface CmsBannerProps extends TypeBannerFields {
  testId?: string;
  classes?: string;
  onClickCta?: () => void;
}

const CmsBanner: React.FC<CmsBannerProps> = ({
  headerText,
  headerSize = HeaderSize.H1,
  headerAlignment,
  paragraphText,
  paragraphAlignment,
  colorScheme,
  borderType,
  cta,
  icon,
  image,
  testId,
  onClickCta,
  classes,
}) => {
  const getIcon = (iconType: keyof typeof iconComponents) => {
    const IconComponent = iconComponents[iconType];
    if (!IconComponent) return null;

    return (
      <>
        <IconComponent
          width={ICON_MOBILE_SIZE_MAPPING[headerSize]}
          height={ICON_MOBILE_SIZE_MAPPING[headerSize]}
          classes="md:hidden"
          fill={ICON_STYLES[colorScheme as BannerColorScheme]}
        />
        <IconComponent
          width={ICON_DESKTOP_SIZE_MAPPING[headerSize]}
          height={ICON_DESKTOP_SIZE_MAPPING[headerSize]}
          classes="hidden md:block"
          fill={ICON_STYLES[colorScheme as BannerColorScheme]}
        />
      </>
    );
  };

  return (
    <GenBanner
      classes={classes}
      ctaAttr={
        cta && {
          href: cta.fields.linkUrl,
          cta: cta.fields.linkText,
          blankTarget: cta.fields.blankTarget,
          style: cta.fields.buttonStyle,
          ctaColor: CTA_COLOR_MAPPING[colorScheme],
          onClick: onClickCta,
        }
      }
      headingAttr={{
        size: headerSize as HeaderSize,
        text: headerText,
        align: headerAlignment,
      }}
      paragraphText={paragraphText}
      paragraphAlignment={paragraphAlignment as BannerParagraphAlignment}
      icon={icon && getIcon(icon)}
      image={image && <LuvImage height={72} width={72} imageData={image} />}
      colorScheme={colorScheme as BannerColorScheme}
      borderType={borderType as BannerBorderType}
      testId={testId}
    />
  );
};

export default React.memo(CmsBanner);
